import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { YEAR2_WEEKS } from '@/data/year2';
import { getSessionForDay, applyPathBOverrides } from '@/data/sessionBuilder';
import { resolveYear1Day } from '@/data/resolveYear1Day';
import { getNewMovementForDay } from '@/data/newMovementDays';
import { useApp } from '@/contexts/AppContext';
import { t } from '@/locales/strings';
import { supabase } from '@/lib/supabase';
import { HeroSkeleton, SessionCardSkeleton } from '@/components/ui/Skeleton';
import TweenNumber from '@/components/ui/TweenNumber';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import useAppBadge from '@/hooks/useAppBadge';
import { currentSeason, hemisphereFor } from '@/utils/season';
import { vibrateTap } from '@/utils/sound';
import BottomNav from '@/components/training/BottomNav';
import Year1NewMovementCard from '@/components/training/Year1NewMovementCard';
import ProgramComplete from '@/components/training/ProgramComplete';
import OnboardingFlow from '@/components/onboarding/OnboardingFlow';
import CoachCard from '@/components/home/CoachCard';
import StreakShareModal from '@/components/home/StreakShareModal';
import WeekDots from '@/components/home/WeekDots';
import { cyclePhase, resolveCycleLength } from '@/logic/cycle';
import { sessionRecommendationForPhase, toneColor } from '@/logic/cycleSessionRec';
import EndOfCycleLetter from '@/components/training/EndOfCycleLetter';
import CycleSetupNudge from '@/components/training/CycleSetupNudge';
import StreakMilestoneOverlay, { useStreakMilestone } from '@/components/home/StreakMilestoneOverlay';
import ChapterCompleteOverlay, { useChapterMilestone } from '@/components/home/ChapterCompleteOverlay';
import FirstRunTour, { isFirstRunTourSeen } from '@/components/home/FirstRunTour';
import AppGuide from '@/components/home/AppGuide';
import WhatsNewSheet, { WhatsNewBanner, hasUnreadWhatsNew } from '@/components/home/WhatsNew';
import { isHelpcueArmed, clearHelpcue } from '@/utils/introState';
import useOverlayQueue from '@/hooks/useOverlayQueue';
import { firstNameFromUser } from '@/utils/nameFromUser';

// FlameIcon — small flame glyph for the streak chip. Filled gradient
// gives it a warm "burning" feel without animation. Sized to sit
// inside the existing wh-status-pill chrome.
function FlameIcon() {
  return (
    <svg width="13" height="14" viewBox="0 0 16 18" aria-hidden="true">
      <defs>
        <linearGradient id="bbFlameG" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stopColor="#ffb87a" />
          <stop offset="100%" stopColor="#a85c4b" />
        </linearGradient>
      </defs>
      <path
        d="M 8 17 C 3.6 17 1 14 1 10.5 C 1 7.5 3 5.5 5 4 C 5.5 6 6.5 6.7 7.5 6.5 C 7 4 9 1.5 11.5 0 C 11 2.5 13 4 13.5 7 C 14.6 9 14.4 12.5 12 15 C 11 16 9.5 17 8 17 Z"
        fill="url(#bbFlameG)"
      />
    </svg>
  );
}
import './WeekHome.css';

// ── Constants ──

// Hero-band decorative rings — absolute-positioned, aria-hidden. The
// three concentric ellipses echo the brand 3-ring motif and give the
// mauve band an anchor point that isn't just text.
function SessionHeroRings() {
  return (
    <svg
      className="wh-session-hero-rings"
      viewBox="0 0 160 160"
      aria-hidden="true"
    >
      <ellipse cx="80" cy="80" rx="66" ry="46" stroke="rgba(255,255,255,0.85)" strokeWidth="1" fill="none" />
      <ellipse cx="80" cy="80" rx="46" ry="32" stroke="rgba(255,255,255,0.7)" strokeWidth="1" fill="none" />
      <ellipse cx="80" cy="80" rx="26" ry="18" stroke="rgba(255,255,255,0.6)" strokeWidth="1" fill="none" />
      <circle cx="80" cy="80" r="2" fill="rgba(255,255,255,0.85)" />
    </svg>
  );
}

// ── Design-system icon primitives (inline SVG, primary stroke) ──
// All icons share the same geometry conventions per the design rules:
// 20x20 canvas, viewBox 0 0 24 24, stroke=currentColor (resolves to
// var(--primary) so themes recolour the entire icon set), strokeWidth
// 1.6, round caps/joins. Kept local to this file so a design change
// in one screen never leaks into another.
const ICON_BASE = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  style: { color: 'var(--primary)' },
};
const IconBook = () => (
  <svg {...ICON_BASE} aria-hidden="true">
    <path d="M4 4h9a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4z" />
    <path d="M4 4v13a3 3 0 0 0 3 3" />
    <path d="M8 8h5M8 12h5" />
  </svg>
);
const IconSpark = () => (
  <svg {...ICON_BASE} aria-hidden="true">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" />
  </svg>
);
const IconHeart = () => (
  <svg {...ICON_BASE} aria-hidden="true">
    <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />
  </svg>
);
const IconCompass = () => (
  <svg {...ICON_BASE} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M15.5 8.5l-2 5-5 2 2-5z" />
  </svg>
);
const IconCheck = () => (
  <svg {...ICON_BASE} aria-hidden="true">
    <path d="M5 12.5l4 4 10-10" />
  </svg>
);
const IconFlag = () => (
  <svg {...ICON_BASE} aria-hidden="true">
    <path d="M5 21V4M5 4h12l-2 4 2 4H5" />
  </svg>
);
// Shared bullet-card surface. Each row is a 32x32 pale-rose disc with an
// inline SVG, followed by the text body. Cards stack vertically with 8px
// gaps so the list reads as a connected block on the home page.
function BulletCard({ icon, children }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      background: 'var(--surface, #FFFFFF)',
      border: '1px solid var(--border, #E8DDD4)',
      borderRadius: 12,
      padding: '14px 16px',
    }}>
      <span style={{
        width: 32, height: 32, borderRadius: 16,
        background: 'var(--primary-pale, #F5D6E0)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </span>
      <span style={{
        flex: 1,
        fontFamily: 'var(--font-body)',
        fontSize: 14,
        color: 'var(--dark-brown)',
        lineHeight: 1.5,
      }}>
        {children}
      </span>
    </div>
  );
}

// Split a paragraph into sentence-sized chunks so we can render it as a
// vertical stack of bullet cards per the design rules. Keeps trailing
// punctuation, ignores empty fragments, and returns the original string
// wrapped in a single-element array when only one sentence is present.
function splitSentences(text) {
  if (!text) return [];
  const parts = String(text)
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : [String(text).trim()];
}

// ── ReflectionCard ──
// Unified card for the daily diary prompt. Year 1 and Year 2 used to
// have two parallel inline implementations with slightly different
// padding, icon sizes, and font sizes; this component consolidates
// them. Renders a "done" confirmation when the user has already
// written today's entry, otherwise a tappable prompt that routes
// to /reflection. Returns null while we don't yet know the diary
// state so the card doesn't pop in then change shape.
function ReflectionCard({ hasEntry, lang, navigate, marginBottom = 0 }) {
  if (hasEntry === null || hasEntry === undefined) return null;
  if (hasEntry) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid var(--border, #E8DDD4)',
        padding: '12px 14px',
        marginBottom,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <span style={{
          width: 28, height: 28, borderRadius: 14,
          background: '#e8f5e9',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: 13,
          color: '#2e7d32',
        }}>{'✓'}</span>
        <span style={{
          fontFamily: 'var(--font-body)',
          fontSize: 13.5,
          color: 'var(--warm-brown)',
        }}>
          {t('reflection_diary_done', lang)}
        </span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => navigate('/reflection')}
      style={{
        width: '100%',
        textAlign: 'left',
        background: '#fff',
        borderRadius: 12,
        border: '1px solid var(--border, #E8DDD4)',
        borderLeft: '3px solid var(--primary)',
        padding: '12px 14px',
        marginBottom,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        transition: 'opacity 0.1s, transform 0.1s',
      }}
    >
      <span style={{
        width: 30, height: 30, borderRadius: 15,
        background: 'var(--primary-pale, #F5D6E0)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}><IconBook /></span>
      <div style={{ flex: 1 }}>
        <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: 13.5,
          color: 'var(--dark-brown)',
          lineHeight: 1.3,
        }}>
          {t('reflection_diary_prompt', lang)}
        </div>
        <div style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 11,
          color: 'var(--warm-brown)',
          marginTop: 2,
        }}>
          {t('reflection_diary_tap', lang)}
        </div>
      </div>
      <span style={{
        fontSize: 18,
        color: 'var(--warm-brown)',
        fontWeight: 300,
        flexShrink: 0,
      }}>{'›'}</span>
    </button>
  );
}

// ── AppNoteCard ──

function AppNoteCard({ weekData, userProgress, lang }) {
  const key = `bebold_note_wk${userProgress.current_week}_yr${userProgress.current_year}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem(key); } catch { return false; }
  });
  const [dismissing, setDismissing] = useState(false);

  const text = lang === 'pt' ? weekData.appNote_pt : weekData.appNote;
  if (!text || dismissed) return null;

  const handleX = () => {
    setDismissing(true);
    try { localStorage.setItem(key, '1'); } catch {}
    setTimeout(() => setDismissed(true), 200);
  };

  const sentences = splitSentences(text);
  const icons = [<IconSpark key="i0" />, <IconHeart key="i1" />, <IconCompass key="i2" />];

  return (
    <div className={`wh-appnote${dismissing ? ' dismissing' : ''}`}>
      <button type="button" className="wh-appnote-x" onClick={handleX} aria-label={t('dismiss_btn', lang)}>{'\u2715'}</button>
      <div className="wh-appnote-label">{t('appnote_label', lang)}</div>
      {sentences.length > 1 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {sentences.map((s, i) => (
            <BulletCard key={i} icon={icons[i % icons.length]}>{s}</BulletCard>
          ))}
        </div>
      ) : (
        <div className="wh-appnote-body" style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{
            width: 32, height: 32, borderRadius: 16,
            background: 'var(--primary-pale, #F5D6E0)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <IconSpark />
          </span>
          <span style={{ flex: 1 }}>{text}</span>
        </div>
      )}
    </div>
  );
}

// ── NewMovementCard ──

function NewMovementCard({ weekData, lang }) {
  const name = lang === 'pt' ? weekData.newMovement_pt : weekData.newMovement;
  if (!name) return null;
  const sentences = splitSentences(t('new_movement_body', lang));
  const icons = [<IconSpark key="i0" />, <IconCompass key="i1" />, <IconHeart key="i2" />];
  return (
    <div className="wh-ctx wh-ctx--new">
      <div className="wh-ctx-label">{t('new_movement_label', lang)}</div>
      <div className="wh-ctx-title">{name}</div>
      {sentences.length > 1 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {sentences.map((s, i) => (
            <BulletCard key={i} icon={icons[i % icons.length]}>{s}</BulletCard>
          ))}
        </div>
      ) : (
        <div className="wh-ctx-body" style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{
            width: 32, height: 32, borderRadius: 16,
            background: 'var(--primary-pale, #F5D6E0)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <IconSpark />
          </span>
          <span style={{ flex: 1 }}>{sentences[0]}</span>
        </div>
      )}
    </div>
  );
}

// ── MilestoneCard ──

function MilestoneCard({ weekData, userProgress, lang }) {
  // Hooks must run on every render regardless of whether we eventually
  // render the card, so derive `items` and call useState unconditionally.
  const items = Array.isArray(weekData?.milestone) ? weekData.milestone : [];
  const [checked, setChecked] = useState(() => items.map(() => false));

  if (!weekData?.milestone) return null;
  if ((userProgress.sessions_completed_this_week || 0) < weekData.freq) return null;

  const allDone = checked.length > 0 && checked.every(Boolean);

  const toggle = (i) => setChecked(p => { const n = [...p]; n[i] = !n[i]; return n; });

  const subSentences = splitSentences(t('milestone_subtitle', lang));
  const subIcons = [<IconFlag key="s0" />, <IconCheck key="s1" />];

  return (
    <div className="wh-ctx wh-ctx--milestone">
      <div className="wh-ms-title">{t('milestone_title', lang)}</div>
      {subSentences.length > 1 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '6px 0 10px' }}>
          {subSentences.map((s, i) => (
            <BulletCard key={i} icon={subIcons[i % subIcons.length]}>{s}</BulletCard>
          ))}
        </div>
      ) : (
        <div className="wh-ms-sub">{subSentences[0]}</div>
      )}
      {items.map((item, i) => (
        <div className="wh-ms-item" key={i} onClick={() => toggle(i)}>
          <span className={`wh-ms-cb${checked[i] ? ' wh-ms-cb--on' : ''}`}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {checked[i] ? <IconCheck /> : null}
          </span>
          <span className="wh-ms-text">{lang === 'pt' ? (item.pt || item.en) : item.en}</span>
        </div>
      ))}
      {allDone && (
        <div className="wh-ms-done" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 32, height: 32, borderRadius: 16,
            background: 'var(--primary-pale, #F5D6E0)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <IconCheck />
          </span>
          <span>{t('milestone_complete', lang)}</span>
        </div>
      )}
    </div>
  );
}


// Truncates a why-card explanation to the first sentence so
// it fits on the home session card without dominating the
// layout. Falls back to the full string if no period is
// present. Trims trailing whitespace.
function firstSentence(text) {
  if (!text) return '';
  const m = text.match(/^[^.!?]+[.!?]/);
  return (m ? m[0] : text).trim();
}

// Body-weather classifier. Maps the diary's BODY_OPTIONS array
// (`relaxed`, `tense`, `numb`, `aware`, `sensitive`,
// `disconnected`) to one of three reflective tones so the home
// greeting can echo back what the user reported. Strict enough to
// stay accurate (a single tense tag tips into `mixed`, not
// `tight`), gentle enough to not catastrophize.
const SETTLED_TAGS = new Set(['relaxed', 'aware']);
const TIGHT_TAGS   = new Set(['tense', 'numb', 'disconnected']);
function categoriseBodyWeather(tags) {
  if (!Array.isArray(tags) || tags.length === 0) return null;
  let settled = 0, tight = 0;
  for (const t of tags) {
    if (SETTLED_TAGS.has(t)) settled++;
    else if (TIGHT_TAGS.has(t)) tight++;
  }
  if (tight === 0 && settled > 0) return 'settled';
  if (settled === 0 && tight > 0) return 'tight';
  return 'mixed';
}

// ── Main ──

export default function WeekHome() {
  const { user, userProgress, lang, refreshProgress } = useApp();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  useScrollRestoration(scrollRef);
  const [refreshing, setRefreshing] = useState(false);
  const pullY = useRef(null);

  // On-demand OnboardingFlow overlay for Year 1 users who haven't
  // completed onboarding yet. The session card CTA reads "Start Here"
  // and flips this flag on tap instead of navigating to /session.
  // Once OnboardingFlow.onComplete fires, we close the overlay and
  // call refreshProgress(); the session `useEffect` re-runs with the
  // freshly-fetched userProgress (which now includes the real
  // user_path written by PathAssessment), so Day 1 swaps to the
  // correct Path A or Path B content automatically.
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Streak share modal visibility — declared up here (before any
  // early-return guards below) so the hook order stays stable across
  // every render path through this component.
  const [showStreakShare, setShowStreakShare] = useState(false);
  // First-run tour — shown once to every new user. Checks
  // localStorage so a returning user (who has seen it once) skips
  // the overlay. Deferred until after initial mount so the tour
  // doesn't pop over the app's own intro transitions.
  const [showFirstRunTour, setShowFirstRunTour] = useState(false);
  const [showAppGuide, setShowAppGuide] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  // Signup date drives WhatsNew gating: brand-new accounts skip
  // entries dated before they joined so they don't see "new" copy
  // about behaviour they never knew was different.
  const userCreatedAt = user?.created_at;
  const [hasUnread, setHasUnread] = useState(() => hasUnreadWhatsNew(userCreatedAt));
  // Helpcue — a one-time pulse halo on the `?` button shown to a
  // user who just finished the FirstRunTour, pointing them at the
  // re-readable AppGuide. Cleared the first time they tap `?`.
  const [showHelpcue, setShowHelpcue] = useState(false);
  useEffect(() => {
    if (isHelpcueArmed()) setShowHelpcue(true);
  }, [showFirstRunTour]);
  // Milestone detection — auto-fires the celebration overlay when
  // the user lands on 7 / 14 / 30 / 60 / 100 / 200 / 365 days for
  // the first time. Gated in localStorage inside the hook so each
  // milestone fires exactly once.
  const currentStreak = userProgress?.streak_days || 0;
  const { milestone, dismiss: dismissMilestone } = useStreakMilestone(currentStreak);
  const { phaseKey: chapterMilestone, dismiss: dismissChapterMilestone } =
    useChapterMilestone(userProgress);

  // OS home-screen badge: red dot while today's session is pending,
  // cleared after the user trains. Chrome / Edge / Chrome Android PWA
  // only; iOS Safari no-ops. Skipped while vacation mode is on so the
  // user is not nudged during a travel pause.
  useAppBadge({
    lastSessionAt: userProgress?.last_session_at,
    pending: !!userProgress && !userProgress?.vacation_mode,
  });

  const vacationMode = !!userProgress?.vacation_mode;

  // Gentle-return banner. Shown above the session card when the last
  // session was 2 or 3 days ago and the user had an active streak.
  // Streak-preservation lives in SessionScreen.calculateStreak (same
  // 2-3 day forgiveness window). Hidden once the user trains again
  // (gap collapses back to 0 or 1).
  const gentleReturn = useMemo(() => {
    const last = userProgress?.last_session_at;
    if (!last || currentStreak < 1) return null;
    const diffDays = Math.floor(
      (Date.now() - new Date(last).getTime()) / 86400000,
    );
    if (diffDays < 2 || diffDays > 3) return null;
    return { streak: currentStreak, diffDays };
  }, [userProgress, currentStreak]);
  // Overlay queue — declarative priority for the auto-firing
  // overlays that fight for focus on /home. Gates and celebratory
  // overlays come first; the tour only fires when no
  // higher-priority overlay is pending. The tour does not write
  // its seen flag when blocked, so it tries again next visit.
  const activeAutoOverlay = useOverlayQueue({
    onboarding:        showOnboarding,
    streak_milestone:  !!milestone,
    chapter_complete:  !!chapterMilestone,
    first_run_tour:    !isFirstRunTourSeen(),
  });
  useEffect(() => {
    if (activeAutoOverlay !== 'first_run_tour') return;
    const timer = setTimeout(() => setShowFirstRunTour(true), 640);
    return () => clearTimeout(timer);
  }, [activeAutoOverlay]);
  const onboardingDone = userProgress?.onboarding_complete === true;
  // Distinguish "never started onboarding" from "started but didn't
  // finish" so the CTA can read "Resume" instead of "Start Here" when
  // a user bailed mid-flow (e.g. between PathAssessment and
  // BreathingDiscovery). user_intent is the first flag onboarding
  // writes; user_path lands after PathAssessment. Either being set
  // while onboarding_complete is still false means there is real
  // progress to pick up from.
  //
  // progressNormalize defaults user_intent to the string 'unspecified'
  // and never exposes user_path (only the legacy `path`), so we read
  // the raw bb7 blob directly here. Any onboarding step write would
  // have flipped these to a real value before user_progress.data was
  // mirrored back into context.
  const onboardingStarted = !onboardingDone && (() => {
    try {
      const saved = localStorage.getItem('bb7');
      if (!saved) return false;
      const data = JSON.parse(saved);
      const hasIntent = data?.user_intent && data.user_intent !== 'unspecified';
      const hasPath = !!data?.user_path;
      const hasFoundation = !!data?.foundation_lesson_complete;
      const hasBreath = !!data?.breathing_discovery_complete;
      return hasIntent || hasPath || hasFoundation || hasBreath;
    } catch {
      return false;
    }
  })();

  const isYear1 = (userProgress?.current_year || 1) !== 2;

  // Derive a display first-name for the home greeting. Returns null
  // when we cannot get a real-looking name out of the user metadata
  // or email; the greeting then falls back to the generic
  // "Welcome back." form. See src/utils/nameFromUser.js for the
  // rejection rules (too-short / generic handle / contains digits).
  const firstName = firstNameFromUser(user) || '';

  // Diary entry check: query diary_entries for today to decide whether
  // to show the reflection prompt card or the "done" confirmation.
  // Also derive the body-state weather (settled / mixed / tight)
  // from the latest entry's body_feeling array — surfaced as a small
  // eyebrow under the greeting so the home screen reflects what the
  // user reported about their body, closing the data → presentation
  // loop. `null` means we have no entry yet (don't render).
  const [hasDiaryEntry, setHasDiaryEntry] = useState(null);
  const [bodyWeather, setBodyWeather] = useState(null);
  useEffect(() => {
    if (!user?.id || !supabase) return;
    const today = new Date().toISOString().slice(0, 10);
    let cancelled = false;
    // Today's row decides the reflection-card state.
    supabase
      .from('diary_entries')
      .select('id')
      .eq('user_id', user.id)
      .eq('entry_date', today)
      .maybeSingle()
      .then(({ data }) => { if (!cancelled) setHasDiaryEntry(!!data); })
      .catch(() => { if (!cancelled) setHasDiaryEntry(false); });
    // Body weather — read the latest entry from the past 3 days;
    // any older and the signal is too stale to greet the user
    // with. Three days covers a weekend gap without fabricating
    // continuity.
    const threeDaysAgo = new Date(Date.now() - 3 * 86400000).toISOString().slice(0, 10);
    supabase
      .from('diary_entries')
      .select('body_feeling, entry_date')
      .eq('user_id', user.id)
      .gte('entry_date', threeDaysAgo)
      .order('entry_date', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        if (!data?.body_feeling) { setBodyWeather(null); return; }
        try {
          const arr = JSON.parse(data.body_feeling);
          setBodyWeather(categoriseBodyWeather(arr));
        } catch { setBodyWeather(null); }
      })
      .catch(() => { if (!cancelled) setBodyWeather(null); });
    return () => { cancelled = true; };
  }, [user]);

  // Year 2 still uses week-based metadata. Year 1 no longer reads weekData —
  // hero, cards, and session all come from resolveYear1Day() instead.
  const weekData = useMemo(() => {
    if (!userProgress) return null;
    if (userProgress.current_year !== 2) return null;
    return YEAR2_WEEKS[userProgress.current_week - 1] ?? null;
  }, [userProgress]);

  // Session lives in state because Year 1 resolution is async (dynamic import
  // of a per-month raw file). Year 2 stays synchronous inside the effect.
  const [session, setSession] = useState(null);

  useEffect(() => {
    // TEMP DIAGNOSTIC (PR #25 white-screen). Logs every entry into the
    // session resolution effect so we can tell which branch the user hits
    // and whether the dynamic import resolves. Remove once fixed.
     
    console.log('[WeekHome] session effect run', {
      hasUser: !!user,
      hasUserProgress: !!userProgress,
      current_year: userProgress?.current_year,
      current_day: userProgress?.current_day,
      current_week: userProgress?.current_week,
      path: userProgress?.path,
      user_path_attr: user?.path,
      hasWeekData: !!weekData,
    });

    if (!user || !userProgress) { setSession(null); return; }
    let cancelled = false;

    async function run() {
      if (userProgress.current_year === 2) {
        if (!weekData) { if (!cancelled) setSession(null); return; }
        const dayName = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        let s = getSessionForDay(weekData, dayName, user.path || 'A');
        if (user.path === 'B') s = applyPathBOverrides(weekData, s);
         
        console.log('[WeekHome] Year 2 session resolved', { type: s?.type });
        if (!cancelled) setSession(s);
      } else {
        // Robust default: Number.isFinite handles undefined, null, NaN, 0.
        const raw = userProgress.current_day;
        const dayNumber = Number.isFinite(raw) && raw >= 1 ? raw : 1;
        // Year 1 reads the path from userProgress (AppContext already maps
        // vanilla `data.path` to 'A' / 'B'). The Supabase auth `user` object
        // has no `.path` attribute so user.path was always undefined here.
        const pathArg = userProgress?.path || 'A';
         
        console.log('[WeekHome] resolving Year 1 day', { dayNumber, pathArg });
        try {
          const s = await resolveYear1Day(dayNumber, pathArg);
           
          console.log('[WeekHome] Year 1 session resolved', {
            day: s?.day,
            title: s?.title,
            exerciseCount: s?.exercises?.length,
            meta: s?.meta,
          });
          if (!cancelled) setSession(s);
        } catch (err) {
           
          console.error('[WeekHome] Year 1 resolution failed', err);
          if (!cancelled) setSession(null);
        }
      }
    }

    run();
    return () => { cancelled = true; };
  }, [weekData, user, userProgress]);

  // Pull-to-refresh — iOS pattern. While the user pulls down from the
  // top, we drive the spinner's opacity + rotation in real time
  // (imperative DOM mutation, zero re-renders per frame). Past the
  // 64px threshold we commit the refresh.
  const spinnerRef = useRef(null);
  const PULL_THRESHOLD = 64;
  function paintPullProgress(delta) {
    if (!spinnerRef.current) return;
    const progress = Math.max(0, Math.min(1, delta / PULL_THRESHOLD));
    // Reveal as you pull (height + opacity), and rotate the indicator
    // up to 270° so committing the refresh feels like turning a key.
    spinnerRef.current.parentElement.style.height = `${Math.min(56, delta * 0.7)}px`;
    spinnerRef.current.style.opacity = String(progress);
    spinnerRef.current.style.transform = `rotate(${progress * 270}deg)`;
  }
  function clearPullProgress() {
    if (!spinnerRef.current) return;
    spinnerRef.current.parentElement.style.height = '';
    spinnerRef.current.style.opacity = '';
    spinnerRef.current.style.transform = '';
  }
  const onTouchStart = useCallback((e) => {
    if (scrollRef.current?.scrollTop === 0) pullY.current = e.touches[0].clientY;
  }, []);
  const onTouchMove = useCallback((e) => {
    if (pullY.current === null || refreshing) return;
    const delta = e.touches[0].clientY - pullY.current;
    if (delta <= 0) return;
    paintPullProgress(delta);
    if (delta > PULL_THRESHOLD) {
      vibrateTap();
      setRefreshing(true);
      pullY.current = null;
      clearPullProgress();
      refreshProgress().finally(() => setRefreshing(false));
    }
  }, [refreshing, refreshProgress]);
  const onTouchEnd = useCallback(() => {
    pullY.current = null;
    if (!refreshing) clearPullProgress();
  }, [refreshing]);

  // Collapse the logo to a compact wordmark as the user scrolls. Mirrors
  // the iOS large-title pattern: full-size branding at rest, slim header on
  // scroll so the player/session cards keep more vertical room. Logo height
  // is updated imperatively (no React re-render per scroll frame); the
  // hairline shadow toggles via state since it changes only at the
  // threshold.
  const logoRef = useRef(null);
  const [shadowed, setShadowed] = useState(false);
  const onScroll = useCallback((e) => {
    const st = e.target.scrollTop;
    if (logoRef.current) {
      const h = Math.max(36, Math.min(56, 56 - st * 0.5));
      logoRef.current.style.height = `${h}px`;
    }
    const next = st > 16;
    setShadowed((prev) => (prev === next ? prev : next));
  }, []);

  // While userProgress / user are loading, render the actual screen
  // shape (centered logo header + greeting placeholder + session-card
  // placeholder + bottom nav) instead of a generic spinner. The user
  // sees the layout they're about to read; perceived load time drops.
  if (!userProgress || !user) {
    return (
      <div className="wh-screen">
        <header style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
          background: 'var(--bg)', paddingTop: 'var(--safe-top)',
          maxWidth: 520, margin: '0 auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4px 24px' }}>
            <img src="/logo.png" alt="Be Bold" width="56" height="56" style={{ height: 56, width: 'auto', display: 'block' }} />
          </div>
        </header>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <HeroSkeleton />
          <SessionCardSkeleton />
        </div>
        <BottomNav lang={lang} />
      </div>
    );
  }

  // Year 1 graduation: user finished the 360-day curriculum. Render the
  // ProgramComplete screen (rose moment + stats + Paula quote + Year 2
  // reveal) instead of the normal hero + session card. Placed above the
  // Year 2 check and above the `!session` loading gate so day 360 never
  // waits on a Year 1 session resolution that will never be relevant
  // again. Header + BottomNav stay; ProgramComplete owns the scroll body.
  if (isYear1 && (userProgress.current_day || 1) >= 360) {
    return (
      <div className="wh-screen">
        <ProgramComplete />
        <BottomNav lang={lang} />
      </div>
    );
  }

  // Year 2 program complete: no weekData for current_week means the curriculum ended.
  if (userProgress.current_year === 2 && !weekData) {
    return (
      <div className="wh-screen">
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-title)', fontSize: 18, color: 'var(--dark-brown)', lineHeight: 1.5 }}>
            {t('program_complete', lang)}
          </div>
        </div>
        <BottomNav lang={lang} />
      </div>
    );
  }

  // Session still loading (covers Year-1 async resolution and Year-2 sync pre-load).
  if (!session) {
    // Same pattern as the userProgress loading state above — show the
    // shape so the eye doesn't see a void while the session resolves.
    return (
      <div className="wh-screen">
        <header style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
          background: 'var(--bg)', paddingTop: 'var(--safe-top)',
          maxWidth: 520, margin: '0 auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4px 24px' }}>
            <img src="/logo.png" alt="Be Bold" width="56" height="56" style={{ height: 56, width: 'auto', display: 'block' }} />
          </div>
        </header>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <HeroSkeleton />
          <SessionCardSkeleton />
        </div>
        <BottomNav lang={lang} />
      </div>
    );
  }

  const streakDays = userProgress.streak_days || 0;
  // Days since the last session, or null if she has never trained.
  // Drives the gentle welcome-back note below the greeting — a gap
  // is a chance to soften the tone, not to shame the streak.
  const gapDays = (() => {
    const last = userProgress?.last_session_at;
    if (!last) return null;
    const lastT = new Date(last).getTime();
    if (!Number.isFinite(lastT)) return null;
    return Math.floor((Date.now() - lastT) / 86400000);
  })();
  // Today's cycle phase, if the user has entered her last period
  // start date in Profile. Null when not tracking or the stored
  // date is missing / stale (>180 days old). Cycle length comes
  // from the resolver so the home screen agrees with /body, even
  // before logged-history-driven refinement has kicked in.
  const cycleInfo = cyclePhase(
    userProgress?.cycle_last_period_start,
    new Date(),
    resolveCycleLength(userProgress, []),
  );
  const isPathB = userProgress?.path === 'B';
  const week = userProgress.current_week;
  // Year 2: keep week-based Path B gating. Year 1: use the session's month meta
  // (first 30 days of Path B ≈ first 4 weeks of the old model).
  const y1Month = isYear1 ? (session.meta?.month ?? 1) : null;
  // Year 1 exercises come from resolveYear1Day() which normalizes
  // `movement` to the legacy uppercase names ('SQUEEZE', 'RELAX', 'PUSH').
  const showSqueeze = isYear1
    ? (session.exercises?.some((e) => e.movement === 'SQUEEZE') ?? true)
    : (!isPathB || week >= 13);
  const showRelax = isYear1
    ? (session.exercises?.some((e) => e.movement === 'RELAX') ?? true)
    : true;
  const showPush = isYear1
    ? (session.exercises?.some((e) => e.movement === 'PUSH') ?? false)
    : true;
  const squeezeEffort = !isYear1 && isPathB && week >= 5 && week <= 8 ? '20%'
    : !isYear1 && isPathB && week >= 9 && week <= 12 ? '30%' : null;

  // Year 1 only: look up the intro entry for TODAY's day number.
  // `getNewMovementForDay` returns null on non-intro days, so the
  // card renders on exactly one day per movement and disappears the
  // next morning. Path is read from userProgress (not the Supabase
  // auth user object, which has no .path attribute).
  const newMovement = isYear1
    ? getNewMovementForDay(
        Number.isFinite(userProgress?.current_day) && userProgress.current_day >= 1
          ? userProgress.current_day
          : 1,
        userProgress?.path || 'A',
      )
    : null;

  return (
    <div
      className="wh-screen bb-screen-fade-in"
      data-season={currentSeason(new Date(), hemisphereFor(lang))}
    >
      {/* Header — branding only. Status pill (day/streak) lives in the
          hero section so the logo can be the single focal point and so
          the header collapses cleanly on scroll without competing
          elements. */}
      <header style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 50,
        background: 'var(--bg)',
        paddingTop: 'var(--safe-top)',
        maxWidth: 520,
        margin: '0 auto',
        transition: 'box-shadow 220ms cubic-bezier(0.32, 0.72, 0, 1)',
        boxShadow: shadowed ? '0 1px 0 rgba(0,0,0,0.06)' : 'none',
      }}>
        <div style={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4px 24px',
        }}>
          <img
            ref={logoRef}
            src="/logo.png"
            alt="Be Bold"
            width="56"
            height="56"
            style={{
              height: 56,
              width: 'auto',
              display: 'block',
              willChange: 'height',
            }}
          />
          {/* App-guide trigger — anchored to the left of the header,
              mirroring the profile button on the right. The forced
              FirstRunTour covers the visual primitives once; this
              button is the re-readable, opt-in surface that explains
              every other part of the app whenever the user wants. */}
          <button
            type="button"
            onClick={() => {
              if (showHelpcue) {
                setShowHelpcue(false);
                clearHelpcue();
              }
              setShowAppGuide(true);
            }}
            aria-label={t('app_guide_open', lang)}
            style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 40,
              height: 40,
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid color-mix(in srgb, var(--primary) 18%, transparent)',
              color: 'var(--primary-dark, var(--primary))',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              transition: 'background 160ms ease, transform 120ms ease',
            }}
            onPointerDown={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(0.94)'; }}
            onPointerUp={(e) => { e.currentTarget.style.transform = 'translateY(-50%)'; }}
            onPointerLeave={(e) => { e.currentTarget.style.transform = 'translateY(-50%)'; }}
          >
            {/* Pulse halo — only rendered while helpcue is active.
                A double-ring CSS animation that gently expands
                outward, drawing the eye to the otherwise-quiet
                `?` button. Cleared on first tap. */}
            {showHelpcue && (
              <>
                <span aria-hidden="true" className="bb-helpcue-pulse" style={{
                  position: 'absolute',
                  inset: -4,
                  borderRadius: 999,
                  border: '2px solid var(--primary, #c97a68)',
                  pointerEvents: 'none',
                }} />
                <span aria-hidden="true" className="bb-helpcue-pulse" style={{
                  position: 'absolute',
                  inset: -4,
                  borderRadius: 999,
                  border: '2px solid var(--primary, #c97a68)',
                  pointerEvents: 'none',
                  animationDelay: '0.7s',
                }} />
              </>
            )}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7" />
              <circle cx="12" cy="16.5" r="0.6" fill="currentColor" />
            </svg>
          </button>
          {/* Profile shortcut — anchored to the right of the header
              without disturbing the centred logo. One-tap path to
              Profile from anywhere on the home screen, mirroring
              the iOS top-trailing avatar pattern. The bottom nav
              still reaches the same destination; this is a
              convenience for the most common surface. */}
          <button
            type="button"
            onClick={() => navigate('/profile')}
            aria-label={t('nav_profile', lang)}
            style={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 40,
              height: 40,
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid color-mix(in srgb, var(--primary) 18%, transparent)',
              color: 'var(--primary-dark, var(--primary))',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              transition: 'background 160ms ease, transform 120ms ease',
            }}
            onPointerDown={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(0.94)'; }}
            onPointerUp={(e) => { e.currentTarget.style.transform = 'translateY(-50%)'; }}
            onPointerLeave={(e) => { e.currentTarget.style.transform = 'translateY(-50%)'; }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
            </svg>
          </button>
        </div>

        {/* What's-new banner. Renders only when there is an unread
            entry the user has not yet acknowledged; tapping opens the
            full sheet, which writes the latest id to localStorage on
            close so the banner disappears for that user. Sits inside
            the fixed header so it always visible at the top. */}
        {hasUnread && (
          <WhatsNewBanner
            lang={lang}
            userCreatedAt={userCreatedAt}
            onOpen={() => {
              // Hide the banner immediately on tap (the sheet's
              // open-effect writes the seen-id and the next render
              // would drop the banner anyway, but this avoids a
              // visible flash while the sheet animates in).
              setHasUnread(false);
              setShowWhatsNew(true);
            }}
          />
        )}
      </header>

      {/* Scrollable content */}
      <div
        ref={scrollRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onScroll={onScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 8,
        }}
      >
        {/* Pull-to-refresh indicator. While the user pulls, opacity
            and rotation are driven imperatively from onTouchMove
            (paintPullProgress); once they cross the threshold and
            the refresh fires, the wh-spin-active animation takes
            over for a smooth continuous rotation. iOS pattern. */}
        <div className={`wh-pull-spinner${refreshing ? ' active' : ''}`}>
          <svg
            ref={spinnerRef}
            className={`wh-pull-arc${refreshing ? ' wh-spin-active' : ''}`}
            width="22" height="22" viewBox="0 0 22 22" fill="none"
            aria-hidden="true"
          >
            {/* faint ring shows total travel */}
            <circle cx="11" cy="11" r="9" stroke="var(--primary-pale)" strokeWidth="2" fill="none" />
            {/* arc — when not refreshing, shown as a 3/4 sweep
                rotating with pull progress; when refreshing, the
                wh-spin-active class spins the whole svg so the arc
                appears to rotate continuously. */}
            <path
              d="M 11 2 A 9 9 0 0 1 20 11"
              stroke="var(--primary)"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </div>

        {/* Hero section. Top padding uses safe-top so notched devices
            don't bury the eyebrow under the fixed header. The chips
            row (streak + cycle) sits above the greeting so the eye
            lands on the personal greeting as the focal element. */}
        <section style={{ padding: `calc(${hasUnread ? 120 : 76}px + var(--safe-top, 0px)) 24px 14px` }}>
          {/* Top chips row — streak / day pill on the left, optional
              cycle chip on the right. Grouping them horizontally
              compresses the top of the screen and lets the greeting
              and session card breathe. */}
          <div className="bb-stagger-1" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 10,
            minHeight: 28,
          }}>
            {streakDays >= 1 ? (
              // Streak chip — flame icon + day count, tappable to share.
              // Wrapped in a real button so the share sheet opens on tap
              // (matches the Snapchat / Duolingo pattern that drives
              // the daily-streak habit loop).
              <button
                type="button"
                onClick={() => setShowStreakShare(true)}
                className="wh-status-pill"
                style={{
                  appearance: 'none',
                  cursor: 'pointer',
                  margin: 0,
                  border: '1px solid color-mix(in srgb, var(--primary) 24%, transparent)',
                  background: 'color-mix(in srgb, var(--primary-pale, #fbeae7) 75%, transparent)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  WebkitTapHighlightColor: 'transparent',
                }}
                aria-label={t('streak_share_aria', lang).replace('{n}', String(streakDays))}
              >
                <FlameIcon />
                <span style={{
                  fontFamily: 'var(--font-ui)',
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: '0.08em',
                  color: 'var(--primary-dark, #a85c4b)',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  <TweenNumber value={streakDays} /> {t('streak_days_label', lang)}
                </span>
              </button>
            ) : (
              <div className="wh-status-pill" style={{ margin: 0 }}>
                <span className="wh-status-pill-dot" aria-hidden="true" />
                <span style={{
                  fontFamily: 'var(--font-ui)',
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: 'var(--primary)',
                }}>
                  {(() => {
                    const tpl = t('weekhome_day_pill', lang);
                    const day = userProgress?.current_day || 1;
                    if (!tpl.includes('{n}')) return tpl;
                    const [before = '', after = ''] = tpl.split('{n}');
                    return (
                      <>
                        {before}<TweenNumber value={day} />{after}
                      </>
                    );
                  })()}
                </span>
              </div>
            )}

            {/* Cycle phase chip — shown only when the user has opted
                in by entering her last-period start date in Profile.
                Sits on the right of the chips row so it does not push
                the greeting down a line. */}
            {cycleInfo && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'color-mix(in srgb, var(--primary-pale, #fbeae7) 55%, transparent)',
                border: '1px solid color-mix(in srgb, var(--primary, #c97a68) 22%, transparent)',
                fontFamily: 'var(--font-ui)',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--primary-dark, #a85c4b)',
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: 'var(--primary, #c97a68)',
                }} aria-hidden="true" />
                {t('cycle_chip_day', lang)
                  .replace('{day}', String(cycleInfo.day))
                  .replace('{phase}', t('cycle_phase_' + cycleInfo.phase, lang))}
              </div>
            )}
          </div>

          {/* Week dots — 7-circle row showing which days of the current
              week the user has trained. Completed days filled, today
              ringed, missed days hollow, future days dim. Derived from
              streak + last_session_at so the surface always agrees
              with the streak chip above it. */}
          <WeekDots
            streakDays={streakDays}
            lastSessionAt={userProgress?.last_session_at}
            lang={lang}
          />

          {/* First-day welcome flourish. Day 1, no streak yet, no
              prior session: the pristine moment of arrival deserves
              its own micro-headline above the greeting. Disappears
              once the user has trained once or moved past day 1. */}
          {isYear1 && (userProgress?.current_day || 1) === 1 && streakDays === 0 && !userProgress?.last_session_at && (
            <span className="bb-stagger-2" style={{
              display: 'block',
              fontFamily: 'var(--font-ui)',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              color: 'var(--primary-dark, var(--primary))',
              margin: '4px 0 2px',
            }}>
              {t('weekhome_first_day_eyebrow', lang)}
            </span>
          )}

          {/* Warm personalized greeting — the focal element of the
              hero zone. Uses the first-name derivation above; falls
              back to a generic greeting when no name is available. */}
          <h1 className="bb-stagger-2" style={{
            fontFamily: 'var(--font-title)',
            fontStyle: 'italic',
            fontSize: 26,
            fontWeight: 500,
            fontVariationSettings: '"opsz" 96',
            color: 'var(--dark-brown)',
            lineHeight: 1.15,
            letterSpacing: '-0.015em',
            margin: '6px 0 6px',
            textWrap: 'balance',
          }}>
            {firstName
              ? t('weekhome_greeting_named', lang).replace('{name}', firstName)
              : t('weekhome_greeting_generic', lang)}
          </h1>

          {/* Body-state weather. A single quiet line that reflects
              the user's most recent diary tags back at her: settled
              / mixed / tight. Closes the loop between what she
              reports and what the app reflects. Only renders when
              there is a fresh-enough entry (≤3 days). */}
          {bodyWeather && (
            <p className="bb-stagger-2" style={{
              margin: '0 0 4px',
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.04em',
              color: 'var(--warm-brown)',
              textTransform: 'lowercase',
              fontStyle: 'italic',
              opacity: 0.85,
            }}>
              {t(`weekhome_body_weather_${bodyWeather}`, lang)}
            </p>
          )}

          {/* Intent chip — mirrors the reason the user gave during
              onboarding ("Practising for recovery." / "Practising
              for intimacy." / etc.). Quiet, italic, low-saturation
              so it reads as a thread back to the original reason
              rather than a label. Skipped for `unspecified` and
              missing values. Only one of weather / intent renders
              at a time so the greeting block stays compact:
              weather (recent diary signal) wins because it's
              fresher data; intent fills in when there's no weather. */}
          {!bodyWeather && userProgress?.user_intent
            && userProgress.user_intent !== 'unspecified' && (
            <p className="bb-stagger-2" style={{
              margin: '0 0 4px',
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.04em',
              color: 'var(--warm-brown)',
              textTransform: 'lowercase',
              fontStyle: 'italic',
              opacity: 0.85,
            }}>
              {t(`home_intent_chip_${userProgress.user_intent}`, lang)}
            </p>
          )}

          {/* Date eyebrow sits below the greeting, in the editorial
              magazine pattern: dateline under the headline. */}
          <span className="bb-stagger-2" style={{
            display: 'block',
            fontFamily: 'var(--font-ui)',
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            color: 'var(--warm-brown)',
          }}>
            {new Date().toLocaleDateString(lang === 'pt' ? 'pt-BR' : 'en-US', {
              weekday: 'long', month: 'long', day: 'numeric'
            })}
          </span>

          {/* Gentle welcome-back note for returning users. Two tiers:
              2-14 days → "pick up gently"; 15+ days → "we kept your
              place". Deliberately silent at 0-1 day gaps so a regular
              daily user never sees it. The streak count above already
              reflects gap reality; this line chooses warmth over
              shame. */}
          {gapDays != null && gapDays >= 2 && gapDays < 365 && (
            <p className="bb-stagger-3" style={{
              fontFamily: 'var(--font-body)',
              fontSize: 14,
              lineHeight: 1.5,
              color: 'var(--ink-dim, #6b5560)',
              margin: '10px 0 0',
              textWrap: 'balance',
            }}>
              {t(
                gapDays >= 15 ? 'weekhome_welcome_back_long' : 'weekhome_welcome_back_short',
                lang,
              ).replace('{days}', String(gapDays))}
            </p>
          )}
        </section>

        {/* Vacation mode banner. Shown whenever the flag is on so the
            user has a constant reminder she is on pause, without the
            home page nagging her to train. Toggles off from Profile or
            automatically on the next completed session. */}
        {vacationMode && (
          <section style={{ padding: '0 24px' }}>
            <div
              role="status"
              style={{
                padding: '14px 16px',
                marginBottom: 14,
                borderRadius: 14,
                background: 'var(--primary-pale, #F5D6E0)',
                border: '1px solid color-mix(in srgb, var(--primary) 22%, transparent)',
              }}
            >
              <div style={{
                fontFamily: 'var(--font-ui)',
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--primary, #A8003F)',
                marginBottom: 2,
              }}>
                {t('vacation_active_eyebrow', lang)}
              </div>
              <div style={{
                fontFamily: 'var(--font-body)',
                fontSize: 13.5,
                lineHeight: 1.5,
                color: 'var(--dark-brown, #2D1A1F)',
              }}>
                {t('vacation_active_body', lang)}
              </div>
            </div>
          </section>
        )}

        {/* Session Card — primary action, sits directly under the
            greeting hero. AppNote, CoachCard, and the reflection
            companion are rendered below it so the user reaches the
            Start button without scrolling past supplementary
            content first. */}
        <section style={{ padding: '0 24px 12px' }}>
          {/* Gentle-return banner. 2 or 3 days since last session with
              an existing streak. Reassures the user that her streak is
              preserved and invites her back. */}
          {gentleReturn && (
            <div
              role="status"
              style={{
                padding: '14px 16px',
                marginBottom: 14,
                borderRadius: 14,
                background: 'var(--primary-pale, #F5D6E0)',
                border: '1px solid color-mix(in srgb, var(--primary) 22%, transparent)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div style={{
                fontFamily: 'var(--font-ui)',
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--primary, #A8003F)',
              }}>
                {t('gentle_return_eyebrow', lang)}
              </div>
              <div style={{
                fontFamily: 'var(--font-body)',
                fontSize: 13.5,
                lineHeight: 1.5,
                color: 'var(--dark-brown, #2D1A1F)',
              }}>
                {t(
                  gentleReturn.diffDays === 2
                    ? 'gentle_return_body_single'
                    : 'gentle_return_body_multi',
                  lang,
                ).replace('{n}', String(gentleReturn.streak))}
              </div>
            </div>
          )}

          {/* Year 1 only: shows on the single day a new named movement
              is introduced (per NEW_MOVEMENT_DAYS). Null on all other
              days. Year 2 has its own weekly NewMovementCard rendered
              further below, outside this section. */}
          {isYear1 && newMovement && (
            <Year1NewMovementCard movement={newMovement} />
          )}
          {(!isYear1 && session.type === 'REST') ? (
            <div style={{
              background: 'rgba(232, 221, 208, 0.5)',
              border: '1px solid var(--beige)',
              borderRadius: 24,
              padding: 32,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>{'\uD83C\uDF19'}</div>
              <h2 style={{
                fontFamily: 'var(--font-title)',
                fontSize: 36,
                color: 'var(--dark-brown)',
                margin: '0 0 8px',
              }}>
                {t('session_card_rest_title', lang)}
              </h2>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: 16,
                color: 'var(--warm-brown)',
                margin: 0,
                lineHeight: 1.6,
              }}>
                {t('session_card_rest_body', lang)}
              </p>
            </div>
          ) : (
            <>
            {/* First-time setup nudge: invites users who have
                trained but never logged a cycle to set one up.
                Renders only when both conditions match AND the
                user has not already dismissed (localStorage
                sentinel). */}
            <CycleSetupNudge onSetUp={() => navigate('/calendar')} />

            {/* End-of-cycle body letter, the moment of recognition
                that closes the loop on a cycle. Renders only when
                the user has just started a new period AND her
                previous cycle had logged floor sensations to
                summarise. The card writes a localStorage sentinel
                on dismiss so it never reappears for the same cycle. */}
            <EndOfCycleLetter onSeeCalendar={() => navigate('/calendar')} />
            <div className="wh-session-card motion-shine">
              {/* Mauve hero band — holds the eyebrow + title. The
                  decorative rings anchor the right side, the soft
                  highlight glows top-right. */}
              <div className="wh-session-hero">
                <SessionHeroRings />
                <span className="wh-session-eyebrow">
                  <span className="wh-session-eyebrow-dot" aria-hidden="true" />
                  {lang === 'pt' ? 'PR\u00C1TICA DE HOJE' : "TODAY'S PRACTICE"}
                </span>
                <h2 className="wh-session-title">
                  {isYear1
                    ? (lang === 'pt' ? session.meta?.phase_pt : session.meta?.phase)
                    : (lang === 'pt' ? weekData.theme_pt : weekData.theme)}
                </h2>
                {/* "What we're doing" preview line — pulls the same
                    `session_why_*` content used on the session
                    overview, but truncated to the first sentence
                    so the home card stays glanceable. Lets the
                    user know what they are committing to before
                    tapping START. Only renders for Year 2 where
                    session.type is reliable. */}
                {!isYear1 && session.type && (
                  <p className="wh-session-preview" style={{
                    margin: '8px 0 0',
                    fontFamily: 'var(--font-body)',
                    fontStyle: 'italic',
                    fontSize: 13,
                    lineHeight: 1.4,
                    color: 'rgba(255,255,255,0.92)',
                    textWrap: 'balance',
                    maxWidth: 280,
                  }}>
                    {firstSentence(t('session_why_' + session.type.toLowerCase(), lang))}
                  </p>
                )}
              </div>

              {/* White card body — meta row + movement pills + Start CTA. */}
              <div style={{ padding: '16px 20px 18px' }}>
                {/* Cycle-aware recommendation banner. Renders only
                    when the user has cycle data set up; sits above
                    the meta row so it contextualises today's session
                    before the user reads the type/duration. */}
                {(cycleInfo || userProgress?.menopause_stage) && (
                  <CycleSessionBanner
                    cyclePhase={cycleInfo?.phase}
                    menopauseStage={userProgress?.menopause_stage}
                    lang={lang}
                  />
                )}
                {/* Meta row — session type (Year 2) + duration on a
                    single horizontal line so the eye doesn't have to
                    scan three stacked chips before reaching the
                    Start button. Wraps on tiny viewports. */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginBottom: 12,
                }}>
                  {!isYear1 && (
                    <span style={{
                      background: 'var(--primary)',
                      color: '#fff',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.14em',
                      padding: '5px 12px',
                      borderRadius: 999,
                    }}>
                      {t('session_type_' + session.type.toLowerCase(), lang)}
                    </span>
                  )}
                  {/* Duration badge — short / commitment-friendly. */}
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'var(--primary-pale)',
                    color: 'var(--primary-dark, var(--primary))',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    padding: '4px 11px',
                    borderRadius: 999,
                  }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                    {t('weekhome_session_duration', lang).replace(
                      '{minutes}',
                      String(Math.max(1, Math.round((session.meta?.session_target_seconds || 300) / 60))),
                    )}
                  </span>
                </div>

                {/* Movement pills */}
                <div style={{
                  display: 'flex',
                  gap: 7,
                  flexWrap: 'wrap',
                  marginBottom: 14,
                }}>
                  {isPathB && ((isYear1 && y1Month === 1) || (!isYear1 && week <= 4)) && (
                    <span style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 11,
                      color: 'var(--relax)',
                      fontWeight: 600,
                      width: '100%',
                      marginBottom: 4,
                    }}>
                      {t('session_path_b_banner', lang)}
                    </span>
                  )}
                  {showSqueeze && (
                    <span className="wh-movement-pill wh-movement-pill--squeeze">
                      <span className="wh-movement-pill-dot" aria-hidden="true" />
                      {lang === 'pt' ? 'Apertar' : 'Squeeze'}{squeezeEffort ? ` ${squeezeEffort}` : ''}
                    </span>
                  )}
                  {showRelax && (
                    <span className="wh-movement-pill wh-movement-pill--relax">
                      <span className="wh-movement-pill-dot" aria-hidden="true" />
                      {lang === 'pt' ? 'Relaxar' : 'Relax'}
                    </span>
                  )}
                  {showPush && (
                    <span className="wh-movement-pill wh-movement-pill--push">
                      <span className="wh-movement-pill-dot" aria-hidden="true" />
                      {lang === 'pt' ? 'Empurrar' : 'Push'}
                    </span>
                  )}
                </div>

                {!isYear1 && (
                  <p style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 12,
                    color: 'var(--warm-brown)',
                    margin: '0 0 20px',
                  }}>
                    {t('session_card_progress', lang)
                      .replace('{done}', userProgress.sessions_completed_this_week || 0)
                      .replace('{total}', weekData.freq)}
                  </p>
                )}

                <button
                  type="button"
                  className="wh-session-cta motion-breath"
                  onClick={() => {
                    try { vibrateTap(); } catch { /* noop */ }
                    if (isYear1 && !onboardingDone) {
                      setShowOnboarding(true);
                      return;
                    }
                    navigate('/session', {
                      state: { session, weekData, userProgress, user },
                    });
                  }}
                >
                  {isYear1
                    ? t(onboardingDone ? 'start' : (onboardingStarted ? 'resume' : 'start_here'), lang)
                    : t(session.type === 'GENTLE' ? 'session_card_cta_gentle' : 'session_card_cta', lang)}
                  <span className="wh-session-cta-arrow" aria-hidden="true">{'\u2192'}</span>
                </button>
              </div>
            </div>
            </>
          )}

        </section>

        {/* Reflection card — direct companion to the session card,
            shared across Year 1 and Year 2. Tappable prompt routes
            to /reflection; flips to a "done" state once today's
            entry is written. Hidden until we know diary state so
            the layout doesn't shift. */}
        {session && (
          <section style={{ padding: '0 24px 12px' }}>
            <ReflectionCard
              hasEntry={hasDiaryEntry}
              lang={lang}
              navigate={navigate}
            />
          </section>
        )}

        {/* App Note — Year 2 only (reads weekData.appNote). Rendered
            after the session card so it reads as supplementary
            commentary, not as a wall the user has to step over to
            reach Start. */}
        {!isYear1 && <AppNoteCard weekData={weekData} userProgress={userProgress} lang={lang} />}

        {/* Coach card — founder voice. Default placeholder content
            (photo + name + sample quote) ships with the build so the
            surface is visible immediately; production swaps photo /
            name / title / quote / audioSrc. */}
        <section style={{ padding: '0 24px' }}>
          <CoachCard lang={lang} onTap={() => navigate('/method')} />
        </section>

        {/* Year 1 only: Paula quote + diary reflection + Day progress
            bar. Anchored to the bottom of the viewport via
            marginTop: 'auto' on the flex-column scroll container — on
            short screens the session card sits near the top and these
            supporting elements hug the BottomNav; on taller content,
            the block scrolls naturally with the rest. */}
        {isYear1 && (
          <section style={{ padding: '0 24px 12px' }}>
            {/* Paula quote — left-bordered pale-primary blockquote */}
            <blockquote style={{
              borderLeft: '3px solid var(--primary-fixed)',
              background: 'var(--surface-alt)',
              borderRadius: '0 10px 10px 0',
              padding: '8px 12px',
              margin: '0 0 8px',
              fontFamily: 'var(--font-title)',
              fontStyle: 'italic',
              fontSize: 13,
              lineHeight: 1.4,
              color: 'var(--warm-brown)',
            }}>
              {'\u201C'}{t('paula_quote', lang)}{'\u201D'}
            </blockquote>

            {(() => {
              const totalDays = 360;
              const currentDay = Math.max(1, userProgress?.current_day || 1);
              // Hide the year-progress strip on day 1 — a new user
              // sees "0%" + a hairline-thin bar that reads as a
              // status defect, not as encouragement. Surface this
              // once she has actually moved through the program.
              if (currentDay < 2) return null;
              const pct = Math.min(100, Math.max(0, (currentDay / totalDays) * 100));
              return (
                <div style={{ marginTop: 4 }}>
                  {/* Two labels: Day X of 360 left, percentage right.
                      Sit above the track so the user reads the number
                      first, then sees the visual proof underneath. */}
                  <div style={{
                    marginBottom: 6,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    color: 'var(--warm-brown)',
                  }}>
                    <span>
                      {t('day_of_year_label', lang)
                        .replace('{n}', currentDay)
                        .replace('{total}', totalDays)}
                    </span>
                    <span style={{
                      color: 'var(--primary-dark, var(--primary))',
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                    }}>{Math.round(pct)}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: 6,
                    background: 'var(--primary-pale)',
                    borderRadius: 999,
                    overflow: 'hidden',
                    boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--primary) 8%, transparent)',
                  }}>
                    <div style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, var(--primary-light), var(--primary))',
                      borderRadius: 999,
                      transition: 'width 540ms cubic-bezier(0.25, 0.1, 0.25, 1)',
                    }} />
                  </div>
                </div>
              );
            })()}
          </section>
        )}

        {/* Year 2 only: new movement callout and milestones. The
            week-based schedule strip has been removed for parity with
            Year 1 — the day strip on home was removed across the
            board so the greeting + session card carry the top of the
            screen unbroken. */}
        {!isYear1 && <NewMovementCard weekData={weekData} lang={lang} />}
        {!isYear1 && <MilestoneCard weekData={weekData} userProgress={userProgress} lang={lang} />}

      </div>

      <BottomNav lang={lang} />

      {/* Streak share modal — opens when the streak chip is tapped.
          Renders the user's day count as a 4:5 portrait card and
          uses Web Share API on mobile (with PNG download fallback
          on desktop) so the user can post to IG / TikTok / WhatsApp
          in one tap. */}
      <StreakShareModal
        open={showStreakShare}
        onClose={() => setShowStreakShare(false)}
        streakDays={streakDays}
        lang={lang}
        userName={firstName || undefined}
      />

      {/* Milestone overlay — appears once per milestone (7 / 30 /
          100 / 365). Tapping Share dismisses the overlay and opens
          the existing share modal so the user can post her
          milestone card with one tap. */}
      <StreakMilestoneOverlay
        milestone={milestone}
        lang={lang}
        onClose={dismissMilestone}
        onShare={() => {
          // Dismiss the milestone first, then open share on the next
          // tick so the milestone overlay has a chance to unmount
          // and its useModalA11y focus-return doesn't aim at a
          // button that's already gone.
          dismissMilestone();
          setTimeout(() => setShowStreakShare(true), 0);
        }}
      />

      {/* Chapter-complete overlay — fires once when the user crosses
          into a new chapter of the 5-phase arc. The "Read your
          Body Letter" action routes to the letter so the moment
          has a take-with-you artefact attached. */}
      <ChapterCompleteOverlay
        phaseKey={chapterMilestone}
        lang={lang}
        onClose={dismissChapterMilestone}
        onReadLetter={() => {
          dismissChapterMilestone();
          setTimeout(() => navigate('/body-letter'), 0);
        }}
      />

      {/* First-run tour — teaches canal / flower / breath /
          voice / streak in five short steps. Shown once per
          user, gated in localStorage. */}
      <FirstRunTour
        open={showFirstRunTour}
        lang={lang}
        onClose={() => setShowFirstRunTour(false)}
      />

      <AppGuide
        open={showAppGuide}
        lang={lang}
        hasUnreadWhatsNew={hasUnread}
        onClose={() => setShowAppGuide(false)}
        onReplayTour={() => {
          // Re-run the visual lesson on demand. The seen flag is
          // already true (otherwise this row wouldn't be reachable
          // from a returning user's flow), so finishing again just
          // rewrites it. The helpcue is intentionally not re-armed.
          setShowFirstRunTour(true);
        }}
        onOpenWhatsNew={() => {
          setShowWhatsNew(true);
          setHasUnread(false);
        }}
      />

      <WhatsNewSheet
        open={showWhatsNew}
        lang={lang}
        userCreatedAt={userCreatedAt}
        onClose={() => setShowWhatsNew(false)}
      />

      {/* On-demand onboarding overlay. OnboardingFlow's sub-steps
          (PathAssessment, BreathingDiscovery, FoundationLesson,
          ProgramReveal) each render as position:fixed overlays at
          zIndex:10000, so they cover WeekHome completely without
          extra wrapping. On completion we close the overlay and
          refetch userProgress so the `useEffect` above re-runs and
          pulls the freshly-written user_path via resolveYear1Day. */}
      {showOnboarding && (
        <OnboardingFlow
          user={user}
          onComplete={async () => {
            setShowOnboarding(false);
            try { await refreshProgress(); } catch {}
          }}
          onClose={() => setShowOnboarding(false)}
        />
      )}
    </div>
  );
}

// CycleSessionBanner, contextualises today's planned practice with
// what the user's body is likely doing in this cycle phase. Renders
// inside the session card above the meta row. The recommendation is
// always informational, never overrides the planned session. The
// program already has its own progression; this is a "your body may
// feel X today" note that ties the cycle to the practice.
function CycleSessionBanner({ cyclePhase, menopauseStage, lang }) {
  const rec = sessionRecommendationForPhase(cyclePhase, menopauseStage);
  if (!rec) return null;
  const tone = rec.tone;
  const accent = toneColor(tone);
  return (
    <div style={{
      display: 'flex',
      gap: 10,
      alignItems: 'flex-start',
      padding: '10px 12px',
      marginBottom: 12,
      borderRadius: 12,
      background: `color-mix(in srgb, ${accent} 8%, transparent)`,
      border: `1px solid color-mix(in srgb, ${accent} 22%, transparent)`,
    }}>
      <span style={{
        flexShrink: 0,
        marginTop: 2,
        width: 6, height: 6, borderRadius: '50%',
        background: accent,
      }} aria-hidden="true" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: accent,
          marginBottom: 4,
        }}>
          {t(rec.headlineKey, lang)}
        </div>
        <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          lineHeight: 1.45,
          color: 'var(--dark-brown)',
        }}>
          {t(rec.bodyKey, lang)}
        </div>
      </div>
    </div>
  );
}
